import { useState } from 'react'
import { useDocumentMeta } from '../hooks/useDocumentMeta'

export default function Contact() {
  useDocumentMeta({
    title: 'Kontakt Psykoterapeut Svendborg | Tidsbestilling - Terapien.dk',
    description: 'Kontakt Reg. psykoterapeut i Svendborg Birgitte Vilsgaard på tlf. 28 83 94 88. Samtaler inden for psykoterapi, parterapi i Svendborg og familieterapi på Fyn.',
    image: 'https://assets.auxodev.dk/assets/dc1f0a52-cd31-4baa-b917-63870f7a10df/shutterstock-1756005818.jpg'
  })
  const [formData, setFormData] = useState({
    navn: '',
    email: '',
    telefon: '',
    message: '',
    website_url: '' // Honeypot field
  })
  const [status, setStatus] = useState({ type: '', message: '' })
  const [submitting, setSubmitting] = useState(false)

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSubmitting(true)
    setStatus({ type: '', message: '' })

    // Check honeypot
    if (formData.website_url) {
      // Silent fail for bots
      setStatus({ type: 'success', message: 'Tak for din besked! Vi vender tilbage hurtigst muligt.' })
      setSubmitting(false)
      return
    }

    try {
      const res = await fetch('/_api/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          navn: formData.navn,
          email: formData.email,
          telefon: formData.telefon,
          message: formData.message,
          website_url: formData.website_url
        })
      })

      if (res.ok) {
        setStatus({ type: 'success', message: 'Tak for din besked! Jeg vender tilbage hurtigst muligt.' })
        setFormData({ navn: '', email: '', telefon: '', message: '', website_url: '' })
      } else {
        const errData = await res.json().catch(() => ({}))
        setStatus({
          type: 'error',
          message: errData.message || 'Der skete en fejl under afsendelsen. Prøv venligst igen eller ring direkte.'
        })
      }
    } catch (err) {
      console.error('Submission error:', err)
      setStatus({
        type: 'error',
        message: 'Der kunne ikke oprettes forbindelse til serveren. Kontroller din internetforbindelse og prøv igen.'
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="pt-[72px] min-h-screen bg-brand-offwhite">
      {/* Hero Section */}
      <section className="relative bg-brand-dark text-brand-offwhite py-20 lg:py-28 overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/dc1f0a52-cd31-4baa-b917-63870f7a10df/shutterstock-1756005818.jpg"
            alt="Kom i kontakt - Terapien.dk"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-brand-dark via-brand-dark/85 to-transparent" />

        <div className="max-w-7xl mx-auto px-6 relative z-10 flex flex-col gap-4">
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-brand-sage">
            <i className="fa-solid fa-envelope text-[10px]" /> Kontakt os
          </span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-brand-sand leading-tight">
            Kom i kontakt
          </h1>
          <p className="text-base sm:text-lg font-serif italic text-brand-offwhite/85 max-w-3xl leading-relaxed">
            Her finder du alle praktiske oplysninger og kan sende en direkte besked til mig.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-16">
          {/* Left Column: Contact Details & Prose */}
          <div className="lg:col-span-6 flex flex-col gap-8">
            <div className="flex flex-col gap-4">
              <h2 className="text-2xl sm:text-3xl font-serif font-bold text-brand-dark">
                Psykoterapi, parterapi og familieterapi i Svendborg
              </h2>
              <div className="h-1 w-12 bg-brand-sage rounded-full" />
            </div>

            {/* Scraped Prose Verbatim */}
            <div className="text-brand-gray/80 text-sm sm:text-base leading-relaxed flex flex-col gap-4">
              <p>
                Søger du en psykoterapeut i Svendborg, eller ønsker I parterapi eller familieterapi på Fyn? Send mig gerne en besked på nedenstående formular. Hvis du foretrækker telefonisk kontakt, så ring mig op på telefon 28 83 94 88.
              </p>
              <p>
                Jeg har terapilokaler på adressen Dronningemaen 68 i Svendborg – med gode parkeringsforhold og kort afstand til Svendborg Station, så det er nemt at komme hertil fra hele Sydfyn og resten af Fyn.
              </p>
              <div className="bg-brand-sand/20 border border-brand-sand/40 rounded-2xl p-6 my-2">
                <h3 className="font-serif font-bold text-brand-dark mb-2">Jeg er her for at hjælpe</h3>
                <p className="text-xs sm:text-sm">
                  Hvad enten du har brug for et personligt møde, -et telefonisk- eller et online møde – så mærk efter hvad DU har brug for, så finder vi den rette løsning for dig.
                </p>
                <p className="text-xs sm:text-sm mt-2">
                  Jeg kan tilbyde at møde med dig på Teams, Skype eller Whereby, alt efter hvad du foretrækker.
                </p>
              </div>
            </div>

            {/* Contact Info Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-4">
              <div className="bg-brand-offwhite border border-brand-sand/40 rounded-2xl p-6 shadow-sm flex flex-col items-center text-center">
                <div className="h-10 w-10 rounded-xl bg-brand-sand flex items-center justify-center text-brand-dark text-lg mb-3">
                  <i className="fa-light fa-phone" />
                </div>
                <span className="block text-xs uppercase tracking-wider text-brand-sage mb-1">Ring direkte</span>
                <a href="tel:28839488" className="hover:text-brand-sage font-bold text-brand-dark text-base whitespace-nowrap">28 83 94 88</a>
              </div>

              <div className="bg-brand-offwhite border border-brand-sand/40 rounded-2xl p-6 shadow-sm flex flex-col items-center text-center">
                <div className="h-10 w-10 rounded-xl bg-brand-sand flex items-center justify-center text-brand-dark text-lg mb-3">
                  <i className="fa-light fa-envelope" />
                </div>
                <span className="block text-xs uppercase tracking-wider text-brand-sage mb-1">Send en mail</span>
                <a href="mailto:bgv@terapien.dk" className="hover:text-brand-sage font-bold text-brand-dark text-base break-all">bgv@terapien.dk</a>
              </div>

              <div className="bg-brand-offwhite border border-brand-sand/40 rounded-2xl p-6 shadow-sm flex flex-col items-center text-center">
                <div className="h-12 w-12 rounded-xl bg-brand-sand flex items-center justify-center text-brand-dark text-xl mb-4">
                  <i className="fa-light fa-location-dot" />
                </div>
                <div>
                  <span className="block text-xs uppercase tracking-wider text-brand-sage mb-1">Adresse</span>
                  <span className="block font-bold text-brand-dark text-base">Dronningemaen 68</span>
                  <span className="block text-sm text-brand-gray/70">5700 Svendborg</span>
                </div>
              </div>

              <div className="bg-brand-offwhite border border-brand-sand/40 rounded-2xl p-6 shadow-sm flex flex-col items-center text-center">
                <div className="h-12 w-12 rounded-xl bg-brand-sand flex items-center justify-center text-brand-dark text-xl mb-4">
                  <i className="fa-light fa-clock" />
                </div>
                <div>
                  <span className="block text-xs uppercase tracking-wider text-brand-sage mb-1">Åbningstider</span>
                  <span className="block font-bold text-brand-dark text-base">Tir – Tor: 8.00–17.00</span>
                  <span className="block font-bold text-brand-dark text-base">Fre: 8.00–15.00</span>
                  <span className="block text-sm text-brand-gray/70">Weekender kan aftales individuelt</span>
                </div>
              </div>
            </div>

            {/* Facebook link card */}
            <div className="mt-6 bg-brand-sand/15 border border-brand-sand/40 rounded-2xl p-4 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-brand-sand flex items-center justify-center text-brand-dark text-lg shrink-0">
                  <i className="fa-brands fa-facebook-f" aria-hidden="true" />
                </div>
                <div>
                  <span className="block text-xs uppercase tracking-wider text-brand-sage font-medium">Facebook</span>
                  <span className="block text-sm font-bold text-brand-dark">Følg Terapien.dk på Facebook</span>
                </div>
              </div>
              <a
                href="https://www.facebook.com/GardeVilsgaard"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-brand-dark hover:bg-brand-dark/90 text-brand-offwhite text-xs font-semibold px-4 py-2.5 rounded-full transition-all duration-200 shrink-0"
              >
                <span>Besøg side</span>
                <i className="fa-solid fa-arrow-up-right-from-square text-[10px]" aria-hidden="true" />
              </a>
            </div>
          </div>

          {/* Right Column: Contact Form */}
          <div className="lg:col-span-6">
            <div className="bg-brand-offwhite border border-brand-sand/40 rounded-3xl p-8 lg:p-10 shadow-md">
              <h3 className="font-serif text-2xl font-bold text-brand-dark mb-6">Send en besked</h3>

              <form onSubmit={handleSubmit} className="flex flex-col gap-6 relative">
                {/* Honeypot field (website_url) - ABSOLUTELY HIDDEN from humans */}
                <div className="sr-only absolute" style={{ position: 'absolute', width: '1px', height: '1px', padding: '0', margin: '-1px', overflow: 'hidden', clip: 'rect(0,0,0,0)', border: '0' }}>
                  <label htmlFor="website_url">Website URL</label>
                  <input
                    type="text"
                    id="website_url"
                    name="website_url"
                    value={formData.website_url}
                    onChange={handleChange}
                    autoComplete="off"
                  />
                </div>

                {/* Name */}
                <div className="flex flex-col gap-2">
                  <label htmlFor="navn" className="text-xs font-semibold uppercase tracking-wider text-brand-gray/60">
                    Navn <span className="text-brand-sage">*</span>
                  </label>
                  <input
                    type="text"
                    id="navn"
                    name="navn"
                    required
                    value={formData.navn}
                    onChange={handleChange}
                    className="w-full bg-brand-sand/10 border border-brand-sand/60 rounded-full px-6 py-3.5 text-sm text-brand-dark focus:outline-none focus:border-brand-sage focus:ring-1 focus:ring-brand-sage transition-all duration-150"
                    placeholder="Dit fulde navn"
                  />
                </div>

                {/* Email */}
                <div className="flex flex-col gap-2">
                  <label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider text-brand-gray/60">
                    Email Adresse <span className="text-brand-sage">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full bg-brand-sand/10 border border-brand-sand/60 rounded-full px-6 py-3.5 text-sm text-brand-dark focus:outline-none focus:border-brand-sage focus:ring-1 focus:ring-brand-sage transition-all duration-150"
                    placeholder="din@email.dk"
                  />
                </div>

                {/* Phone */}
                <div className="flex flex-col gap-2">
                  <label htmlFor="telefon" className="text-xs font-semibold uppercase tracking-wider text-brand-gray/60">
                    Telefonnummer
                  </label>
                  <input
                    type="tel"
                    id="telefon"
                    name="telefon"
                    value={formData.telefon}
                    onChange={handleChange}
                    className="w-full bg-brand-sand/10 border border-brand-sand/60 rounded-full px-6 py-3.5 text-sm text-brand-dark focus:outline-none focus:border-brand-sage focus:ring-1 focus:ring-brand-sage transition-all duration-150"
                    placeholder="Aftal opkald (valgfrit)"
                  />
                </div>

                {/* Message */}
                <div className="flex flex-col gap-2">
                  <label htmlFor="message" className="text-xs font-semibold uppercase tracking-wider text-brand-gray/60">
                    Hvad har du på hjertet... <span className="text-brand-sage">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={5}
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full bg-brand-sand/10 border border-brand-sand/60 rounded-2xl px-6 py-4 text-sm text-brand-dark focus:outline-none focus:border-brand-sage focus:ring-1 focus:ring-brand-sage transition-all duration-150 resize-none"
                    placeholder="Beskriv kort hvad du ønsker hjælp til..."
                  />
                </div>

                {/* Status Messages */}
                {status.message && (
                  <div className={`p-4 rounded-xl text-sm leading-relaxed ${
                    status.type === 'success' ? 'bg-brand-sage/20 text-brand-dark border border-brand-sage/30' : 'bg-red-50 text-red-800 border border-red-100'
                  }`}>
                    {status.message}
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-brand-dark hover:bg-brand-dark/90 disabled:bg-brand-dark/50 text-brand-offwhite text-xs font-semibold uppercase tracking-wider py-4 rounded-full transition-all duration-300 hover:shadow-lg hover:scale-[1.01] active:scale-[0.99] flex items-center justify-center gap-2"
                >
                  <span>{submitting ? 'Sender henvendelse...' : 'Kontakt mig'}</span>
                  {!submitting && <i className="fa-solid fa-arrow-right text-xs" />}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="w-full h-[450px] bg-brand-sand/20 border-t border-brand-sand/30 relative">
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2266.368305018698!2d10.606828577263595!3d55.06254134516315!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x464cd04be1b89697%3A0x8821034f8285514f!2sDronningemaen%2068%2C%205700%20Svendborg!5e0!3m2!1sda!2sdk!4v1710000000000!5m2!1sda!2sdk"
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="Terapien Klinik Adresse"
          className="grayscale opacity-80 hover:grayscale-0 hover:opacity-100 transition-all duration-500"
        />
      </section>
    </div>
  )
}
